import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import HeadsetMicOutlined from "@mui/icons-material/HeadsetMicOutlined";
import LockRounded from "@mui/icons-material/LockRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import TaskAltRounded from "@mui/icons-material/TaskAltRounded";
import TrackChangesOutlined from "@mui/icons-material/TrackChangesOutlined";
import { useNavigate } from "react-router-dom";
import useContratosAtivos from "../hooks/useContratosAtivos";

const SALES_CONTACT_URL =
  "https://api.whatsapp.com/send/?phone=5511917442081&text&type=phone_number&app_absent=0";

const PRODUCT_ROUTES = {
  1: "/assistente-vendas",
  2: "/sdr",
  3: "/assistente-conteudo",
  5: "/assistente-pos-venda",
  7: "/task-agent/inicio",
  9: "/agentecontabilidade",
};

const PRODUCT_CATALOG = [
  {
    id: 2,
    nome: "Agente de Pré Venda",
    descricao: "Qualificação inteligente de leads e organização da fila comercial.",
    icon: TrackChangesOutlined,
  },
  {
    id: 1,
    nome: "Agente do Corretor",
    descricao: "Apoio operacional para corretores acompanharem oportunidades e atendimento.",
    icon: ApartmentOutlined,
  },
  {
    id: 5,
    nome: "Agente de Pós-Venda",
    descricao: "Relacionamento com clientes depois da venda, com gestão de demandas e follow-ups.",
    icon: HeadsetMicOutlined,
  },
  {
    id: 3,
    nome: "Agente de Conteúdo",
    descricao: "Central de suporte e conteúdos para atendimento e acompanhamento personalizado.",
    icon: DescriptionOutlined,
  },
  {
    id: 7,
    nome: "TaskAgent",
    descricao: "Gestão de processos, pastas, etapas e tarefas para a operação do time.",
    icon: TaskAltRounded,
  },
  {
    id: 9,
    nome: "Agente de Contabilidade",
    descricao: "Dashboards financeiros, DRE de viabilidade e indicadores contábeis.",
    icon: BarChartRounded,
  },
];

const getContratoProdutoId = (contrato) => Number(contrato?.idProduto ?? contrato?.id_produto ?? 0);

const getContratoRoute = (contrato) => PRODUCT_ROUTES[getContratoProdutoId(contrato)] || "";

const getContratoProdutoNome = (contrato) =>
  contrato?.nomeProduto ||
  contrato?.nome_produto ||
  contrato?.produto ||
  contrato?.nome ||
  "Produto";

const getContratoPlano = (contrato) =>
  contrato?.descricaoPlano ||
  contrato?.descricao_plano ||
  contrato?.nomePlano ||
  contrato?.nome_plano ||
  contrato?.plano ||
  "Plano contratado";

const getContratoConsumo = (contrato) => {
  const consumo = Number(contrato?.atendimentos ?? contrato?.consumo ?? contrato?.utilizado ?? 0);
  const limite = Number(contrato?.limite ?? contrato?.limiteContrato ?? contrato?.limite_contrato ?? 0);
  const percentual = limite > 0 ? (consumo / limite) * 100 : 0;

  return {
    consumo,
    limite,
    percentual,
    percentualLimitado: Math.min(Math.max(percentual, 0), 100),
    excedente: limite > 0 && consumo > limite,
  };
};

const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number(value || 0));

const ContratoRow = ({ contrato, onOpen }) => {
  const produtoNome = getContratoProdutoNome(contrato);
  const plano = getContratoPlano(contrato);
  const { consumo, limite, percentual, percentualLimitado, excedente } = getContratoConsumo(contrato);
  const hasLimite = limite > 0;
  const route = getContratoRoute(contrato);
  const isClickable = Boolean(route);

  return (
    <Paper
      elevation={0}
      component={isClickable ? "button" : "div"}
      type={isClickable ? "button" : undefined}
      onClick={isClickable ? () => onOpen(route) : undefined}
      sx={(theme) => ({
        width: "100%",
        textAlign: "left",
        minHeight: 78,
        px: { xs: 1.8, md: 2.25 },
        py: 1.4,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.62)" : "#ffffff",
        cursor: isClickable ? "pointer" : "default",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:hover": isClickable
          ? {
              borderColor: "primary.main",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 12px 24px rgba(0, 0, 0, 0.22)"
                  : "0 12px 24px rgba(16, 24, 40, 0.08)",
              transform: "translateY(-1px)",
            }
          : undefined,
      })}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1.4, md: 2.5 }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: "primary.main",
              bgcolor: "rgba(102, 126, 234, 0.1)",
              flexShrink: 0,
            }}
          >
            <DescriptionOutlined sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1.2 }} noWrap>
              {produtoNome}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {plano}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.65 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
              Consumo
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: excedente ? "error.main" : "text.secondary",
                fontWeight: 800,
              }}
            >
              {hasLimite
                ? `${formatNumber(consumo)} / ${formatNumber(limite)}`
                : formatNumber(consumo)}
            </Typography>
          </Stack>
          {hasLimite ? (
            <LinearProgress
              variant="determinate"
              value={percentualLimitado}
              aria-label={`Consumo ${Math.round(percentual)}%`}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: "rgba(148, 163, 184, 0.18)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  background: excedente
                    ? "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                },
              }}
            />
          ) : null}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: "flex-start", md: "center" } }}>
          <Chip
            icon={<CheckCircleRounded />}
            label="Vigente"
            size="small"
            sx={{
              bgcolor: "#e8f5e8",
              color: "#2e7d32",
              fontWeight: 800,
              "& .MuiChip-icon": { color: "#2e7d32" },
            }}
          />
          {isClickable ? (
            <Chip
              icon={<OpenInNewRounded />}
              label="Acessar"
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 800,
                color: "primary.main",
                borderColor: "rgba(102, 126, 234, 0.35)",
                "& .MuiChip-icon": { color: "primary.main" },
              }}
            />
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
};

const AvailableProductCard = ({ produto }) => {
  const IconComponent = produto.icon || AccountBalanceWalletOutlined;

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        height: "100%",
        p: { xs: 1.8, md: 2 },
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.46)" : "#ffffff",
        display: "flex",
        flexDirection: "column",
        gap: 1.4,
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:hover": {
          borderColor: "rgba(102, 126, 234, 0.45)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 12px 24px rgba(0, 0, 0, 0.18)"
              : "0 12px 24px rgba(16, 24, 40, 0.07)",
          transform: "translateY(-1px)",
        },
      })}
    >
      <Stack direction="row" spacing={1.35} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            color: "text.secondary",
            bgcolor: "rgba(148, 163, 184, 0.12)",
            flexShrink: 0,
          }}
        >
          <IconComponent sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1.2 }}>
              {produto.nome}
            </Typography>
            <Chip
              icon={<LockRounded />}
              label="Disponível"
              size="small"
              sx={{
                height: 24,
                bgcolor: "rgba(148, 163, 184, 0.12)",
                color: "text.secondary",
                fontWeight: 800,
                flexShrink: 0,
                "& .MuiChip-icon": { color: "text.secondary" },
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
            {produto.descricao}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: "auto", pt: 0.5 }}>
        <Button
          component="a"
          href={SALES_CONTACT_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
          fullWidth
          sx={{ fontWeight: 800, textTransform: "none" }}
        >
          Solicitar contratação
        </Button>
      </Box>
    </Paper>
  );
};

const Contratos = () => {
  const navigate = useNavigate();
  const { contratosAtivos, loading } = useContratosAtivos();
  const contratos = Array.isArray(contratosAtivos) ? contratosAtivos : [];
  const contractedProductIds = new Set(contratos.map(getContratoProdutoId));
  const produtosDisponiveis = PRODUCT_CATALOG.filter((produto) => !contractedProductIds.has(produto.id));

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Contratos
        </Typography>
        <Typography color="text.secondary">
          Consulte os produtos contratados, planos vigentes e consumo atual.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={(theme) => ({
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        })}
      >
        {loading ? (
          <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ minHeight: 82 }}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary">
              Carregando contratos...
            </Typography>
          </Stack>
        ) : contratos.length > 0 ? (
          <Stack spacing={1.25}>
            {contratos.map((contrato, index) => (
              <ContratoRow
                key={`${contrato?.idProduto || contrato?.id_produto || contrato?.nomeProduto || "contrato"}-${index}`}
                contrato={contrato}
                onOpen={navigate}
              />
            ))}
          </Stack>
        ) : (
          <Stack justifyContent="center" sx={{ minHeight: 82 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Nenhum contrato vigente encontrado.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quando houver contratos ativos, eles aparecerão nesta área.
            </Typography>
          </Stack>
        )}
      </Paper>

      {!loading && produtosDisponiveis.length > 0 ? (
        <Box>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary", mb: 0.5 }}>
              Produtos disponíveis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Conheça os módulos que ainda podem ser adicionados ao seu portal.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {produtosDisponiveis.map((produto) => (
              <AvailableProductCard key={produto.id} produto={produto} />
            ))}
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
};

export default Contratos;
